import {useTranslation} from 'react-i18next';
import moment from 'moment/moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Grid from '@mui/material/Grid';
import {FormLabel} from '@mui/material';
import _ from 'lodash';
import config from '../../../app/config';
import {AttendanceCategory} from '../../../controls/agyle/dictionaries';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import {ChiefOfComplaintECDS} from '../../../controls/agyle/ecds';
import EscortSection from '../components/escortSection';
import {useAttendanceDetailsStyles} from '../admitStyles';
import AgyleAccordion from '../../../components/ui/accordion/agyleAccordion';

const AdmissionDetails = (props) => {
  const { t } = useTranslation();
  const classes = useAttendanceDetailsStyles();

  const {
    admission,
    setAdmission,
    continueAttendanceRef,
    arrivalModeOptions,
    isArrivalModeOptionsLoading,
    attendanceSourceOptions,
    isAttendanceSourceOptionsLoading,
    attendanceTypeOptions,
    isAttendanceTypeOptionsLoading,
    locationOptions,
    isLocationOptionsLoading,
    patientOnWhiteboard,
    patientAge,
    attendanceCategoryOptions,
    isBedas,
    admit,
    tempPatient,
    conveyingAmbulanceOptions,
    conveyingAmbulanceLoading,
    bedasRemove,
    isSmall,
  } = props;

  const [
    attendanceDetailsAccordionExpanded,
    setAttendanceDetailsAccordionExpanded,
  ] = useState(true);
  const [escortAccordionExpanded, setEscortAccordionExpanded] = useState(true);

  const isPatientArrivedByAmbulance = useMemo(
    () =>
      admission?.modeOfArrival?.group === "ambulance" ||
      admission?.modeOfArrival?.ambulance,
    [admission?.modeOfArrival],
  );

  useEffect(() => {
    let continueAdmissionAttendanceSource;
    let continueAdmissionArrivalMode;
    let continueAdmissionLocation;
    let continueAdmissionAttendanceType;
    let continueAdmissionClone;

    const defaultAttendanceSource =
      !_.isEmpty(attendanceSourceOptions) &&
      attendanceSourceOptions.find(
        (item) =>
          item.__description ===
          "Self / family / friends / education / work colleague",
      );

    const defaultArrivalMode =
      !_.isEmpty(arrivalModeOptions) &&
      arrivalModeOptions.find(
        (item) =>
          item.__description === "Patient arranged own transport / walk-in",
      );

    const defaultLocation =
      !_.isEmpty(locationOptions) &&
      locationOptions.find((item) => item.__code === "E");

    const bedasPt =
      isBedas ||
      (tempPatient && admit?.tags?.includes("bedas") && !bedasRemove);
    const defaultAttendanceTypeCode = bedasPt ? "P" : "F";
    const defaultAttendanceType =
      !_.isEmpty(attendanceTypeOptions) &&
      attendanceTypeOptions.find(
        (item) => item.__code === defaultAttendanceTypeCode,
      );

    let defaultAttendanceCategory = null;
    if (bedasPt) {
      defaultAttendanceCategory =
        !_.isEmpty(attendanceCategoryOptions) &&
        attendanceCategoryOptions.find((item) => item.__code === "4");
    }

    if (!admission.attendanceSource && !continueAttendanceRef.current) {
      setAdmission((prevState) => ({
        ...prevState,
        attendanceSource: defaultAttendanceSource,
      }));
    }
    if (!admission.modeOfArrival && !continueAttendanceRef.current) {
      setAdmission((prevState) => ({
        ...prevState,
        modeOfArrival: defaultArrivalMode,
      }));
    }
    if (!admission.location && !continueAttendanceRef.current) {
      setAdmission((prevState) => ({
        ...prevState,
        location: defaultLocation,
      }));
    }
    if (!admission.attendanceType && !continueAttendanceRef.current) {
      setAdmission((prevState) => ({
        ...prevState,
        attendanceType: defaultAttendanceType,
        attendanceCategory: defaultAttendanceCategory,
      }));
    }

    if (continueAttendanceRef.current) {
      continueAdmissionClone = _.cloneDeep(continueAttendanceRef.current);

      continueAdmissionArrivalMode =
        !_.isEmpty(arrivalModeOptions) &&
        arrivalModeOptions.find(
          (item) =>
            item.__description ===
            continueAdmissionClone?.arrivalModeDescription,
        );

      continueAdmissionLocation =
        !_.isEmpty(locationOptions) &&
        locationOptions.find(
          (item) => item.__description === continueAdmissionClone?.location,
        );

      continueAdmissionAttendanceType =
        !_.isEmpty(attendanceTypeOptions) &&
        attendanceTypeOptions.find(
          (item) =>
            item.__description ===
            continueAdmissionClone?.attendanceTypeDescription,
        );

      setAdmission((prevState) => ({
        ...prevState,
        chiefComplaint: continueAdmissionClone?.chiefComplaint || null,
        modeOfArrival: continueAdmissionArrivalMode || defaultArrivalMode,
        location: continueAdmissionLocation || defaultLocation,
        attendanceType:
          continueAdmissionAttendanceType || defaultAttendanceType,
        attendanceSource:
          continueAdmissionAttendanceSource || defaultAttendanceSource,
        attendanceCategory: defaultAttendanceCategory,
      }));

      continueAttendanceRef.current = null;
    }
  }, [
    arrivalModeOptions,
    attendanceSourceOptions,
    attendanceTypeOptions,
    locationOptions,
    continueAttendanceRef.current,
    attendanceCategoryOptions,
    isBedas,
  ]);

  const handleChangeSwitch = useCallback(
    (e, name) => {
      let newValue = {
        ...admission,
        [name]: e,
      };

      if (name === "flag111" && !e) {
        newValue = {
          ...newValue,
          flag111Timestamp: null,
        };
      }

      setAdmission(newValue);
    },
    [admission],
  );

  const renderSwitchSection = (
    name,
    label,
    value,
    nullStateSwitch = false,
    redLabel,
    conditionalRender = true,
    alignToRight = false,
  ) => {
    return (
      conditionalRender && (
        <Grid
          item
          xs={12}
          className={alignToRight ? classes.alignToRight : null}
        >
          <div className={classes.groupRoot}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <div className={classes.centerRow}>
                <FormLabel
                  sx={{
                    mr: 3,
                    minWidth: "140px",
                    maxWidth: "140px",
                    color: redLabel && "#FF6868",
                  }}
                >
                  {t(label)}
                </FormLabel>

                <AgyleBlockSwitch
                  value={admission[name]}
                  onChange={(e) => handleChangeSwitch(e, name)}
                />

                {name === "flag111" && (
                  <div style={{ marginLeft: "15px" }}>
                    <AgyleDatePicker
                      value={admission.flag111Timestamp}
                      label={t("Date and Time")}
                      error={admission.flag111 && !admission.flag111Timestamp}
                      disabled={!admission.flag111}
                      type="dateTime"
                      noBorder={false}
                      onChange={(e) => {
                        setAdmission((prevState) => ({
                          ...prevState,
                          flag111Timestamp: e,
                        }));
                      }}
                    />
                  </div>
                )}
              </div>
            </Grid>
            {!_.isEmpty(admission[`${name}User`]) && (
              <div className={classes.userLog}>
                {`${admission[`${name}User`]} ${
                  admission[`${name}Timestamp`]
                    ? moment(admission[`${name}Timestamp`]).format(
                        config.defaultDateTimeFormat,
                      )
                    : ""
                }`}
              </div>
            )}
          </div>
        </Grid>
      )
    );
  };

  const getConveyingAmbulanceOptions = () => {
    if (admission.conveyingAmbulanceOrgId) {
      return (
        _.find(conveyingAmbulanceOptions, {
          __description: admission.conveyingAmbulanceOrgId,
        }) || null
      );
    }
    return null;
  };

  return (
    <Grid container gap={1} sx={{ pt: 2 }}>
      <Grid item xs={12}>
        <AgyleAccordion
          label={t("Attendance Details")}
          expanded={attendanceDetailsAccordionExpanded}
          onChange={() =>
            setAttendanceDetailsAccordionExpanded(
              !attendanceDetailsAccordionExpanded,
            )
          }
        >
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <FormLabel>{t('Chief Complaint')}</FormLabel>

              <ChiefOfComplaintECDS
                placeholder={t('Chief Complaint')}
                value={admission.chiefComplaint}
                onChange={(e, val) => {
                  setAdmission((prevState) => ({
                    ...prevState,
                    chiefComplaint: val,
                  }));
                }}
                size="large"
                noBorder={false}
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <FormLabel>{t('Attendance Date')}</FormLabel>

              <AgyleDatePicker
                placeholder={t('Attendance Date')}
                size={"large"}
                inputFormat={config.defaultDateFormat}
                inputVariant="outlined"
                value={admission.admitDate}
                type="dateTime"
                noBorder={false}
                maxDateTime={moment()}
                onChange={(e) => {
                  setAdmission((prevState) => ({ ...prevState, admitDate: e }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <FormLabel>{t('Attendance Type')}</FormLabel>

              <AgyleDictionaryField
                placeholder={`Attendance Type`}
                size={"large"}
                noBorder={false}
                onChange={(_e, val) =>
                  setAdmission((prevState) => ({
                    ...prevState,
                    attendanceType: val,
                  }))
                }
                options={attendanceTypeOptions}
                value={admission.attendanceType}
                loading={isAttendanceTypeOptionsLoading}
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <FormLabel>{t('Attendance Source')}</FormLabel>

              <AgyleDictionaryField
                placeholder={`Attendance Source`}
                size={"large"}
                onChange={(_e, val) =>
                  setAdmission((prevState) => ({
                    ...prevState,
                    attendanceSource: val,
                  }))
                }
                noBorder={false}
                options={attendanceSourceOptions}
                value={admission.attendanceSource}
                loading={isAttendanceSourceOptionsLoading}
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <FormLabel>{t('Location')}</FormLabel>

              <AgyleDictionaryField
                placeholder={`Location`}
                size={"large"}
                noBorder={false}
                onChange={(_e, val) =>
                  setAdmission((prevState) => ({
                    ...prevState,
                    location: val,
                  }))
                }
                options={locationOptions}
                value={admission.location}
                loading={isLocationOptionsLoading}
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <FormLabel>{t('Arrival Mode')}</FormLabel>

              <AgyleDictionaryField
                placeholder={`Arrival Mode`}
                size={"large"}
                noBorder={false}
                onChange={(_e, val) =>
                  setAdmission((prevState) => ({
                    ...prevState,
                    modeOfArrival: val,
                  }))
                }
                options={arrivalModeOptions}
                value={admission.modeOfArrival}
                loading={isArrivalModeOptionsLoading}
                error={!admission.modeOfArrival}
              />
            </Grid>

            {!patientOnWhiteboard && (
              <Grid item xs={isSmall ? 12 : 6}>
                <FormLabel>{t('History of Presenting Complaint')}</FormLabel>

                <AgyleTextField
                  size={"large"}
                  multiline
                  fullWidth
                  noBorder={false}
                  placeholder={t('History of Presenting Complaint')}
                  value={admission.historyOfPresentingComplaint}
                  onChange={(e) =>
                    setAdmission((prevState) => ({
                      ...prevState,
                      historyOfPresentingComplaint: e.target.value,
                    }))
                  }
                />
              </Grid>
            )}

            <Grid item xs={isSmall ? 12 : 6}>
              <FormLabel>{t('Emergency Care Attendance Category')}</FormLabel>

              <AttendanceCategory
                noBorder={false}
                name="attendanceCategory"
                placeholder={t('Emergency Care Attendance Category')}
                value={admission.attendanceCategory}
                onChange={(_e, val) =>
                  setAdmission((prevState) => ({
                    ...prevState,
                    attendanceCategory: val,
                  }))
                }
                size="large"
                error={!admission.attendanceCategory}
              />
            </Grid>

            <Grid item xs={12}>
              {!!isPatientArrivedByAmbulance && (
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} sm={isSmall ? 12 : 4}>
                    <FormLabel>{t('Ambulance Incident Number')}</FormLabel>

                    <AgyleTextField
                      size={"large"}
                      required
                      fullWidth
                      noBorder={false}
                      variant="outlined"
                      placeholder={t('Ambulance Incident Number')}
                      error={!admission.ambulanceIncidentNumber}
                      value={admission.ambulanceIncidentNumber || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        const valCare = val ? `${val.replace("-", "")}-` : "";

                        setAdmission((prevState) => ({
                          ...prevState,
                          ambulanceIncidentNumber: val,
                          careContactIdentifier: valCare,
                        }));
                      }}
                      type="ambulance"
                    />
                  </Grid>

                  <Grid item xs={12} md={isSmall ? 12 : 4}>
                    <FormLabel>{t('Care Contact Identifier')}</FormLabel>

                    <AgyleTextField
                      size={"large"}
                      required
                      fullWidth
                      noBorder={false}
                      variant="outlined"
                      placeholder={t('Care Contact Identifier')}
                      value={admission.careContactIdentifier || ""}
                      error={!admission.careContactIdentifier}
                      onChange={(e) =>
                        setAdmission((prevState) => ({
                          ...prevState,
                          careContactIdentifier: e.target.value,
                        }))
                      }
                      type="ambulance"
                    />
                  </Grid>

                  <Grid item xs={12} sm={isSmall ? 12 : 4}>
                    <FormLabel>{t('Org Id - Conveying Ambulance Trust')}</FormLabel>

                    <AgyleDictionaryField
                      placeholder={`Org Id - Conveying Ambulance Trust`}
                      size={"large"}
                      noBorder={false}
                      onChange={(_e, val) =>
                        setAdmission((prevState) => ({
                          ...prevState,
                          conveyingAmbulanceOrgId: val?.__description,
                        }))
                      }
                      options={conveyingAmbulanceOptions}
                      error={!admission.conveyingAmbulanceOrgId}
                      value={getConveyingAmbulanceOptions()}
                      required
                      loading={conveyingAmbulanceLoading}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} className={classes.feedback}>
              <FormLabel>
                {t(
                  "Do you consent to being contacted by the Trust to give feedback with the care you receive?",
                )}
              </FormLabel>

              <AgyleBlockSwitch
                value={admission?.feedback}
                onChange={(e) => handleChangeSwitch(e, "feedback")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 8}>
              {renderSwitchSection(
                "flag111",
                config.gpExpected ? "GP Expected Flag" : "111 Flag",
                admission?.flag111,
              )}
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4}>
              {renderSwitchSection(
                "assaultRelated",
                "Is Attendance Assault Related",
                admission.assaultRelated,
                false,
                false,
                config?.attendanceAssaultRelatedFlagEnabled,
                !isSmall,
              )}
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4} sx={{ mt: 0.5 }}>
              {patientAge < 18 &&
                renderSwitchSection(
                  "cpIs",
                  "CP-IS Checked?",
                  admission.cpIs,
                  false,
                  !admission.cpIs,
                )}
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4} sx={{ mt: 0.5 }}>
              {patientAge < 18 &&
                admission.cpIs &&
                renderSwitchSection(
                  "cpIsAlertPresent",
                  "CP-IS Alert Present",
                  admission.cpIsAlertPresent,
                )}
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4}>
              {renderSwitchSection(
                "majorTraumaFlag",
                "Major Trauma",
                admission.majorTraumaFlag,
                false,
                false,
                true,
                !isSmall,
              )}
            </Grid>
          </Grid>
        </AgyleAccordion>

        {patientAge < 18 && (
          <Grid item xs={12}>
            <AgyleAccordion
              label={t("Escort")}
              expanded={escortAccordionExpanded}
              onChange={() =>
                setEscortAccordionExpanded(!escortAccordionExpanded)
              }
            >
              <EscortSection
                admission={admission}
                setAdmission={setAdmission}
                isSmall={isSmall}
              />
            </AgyleAccordion>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default AdmissionDetails;
